import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../../Navbar';
import PageTitle from '../../../components/PageTitle';
import ShareGuideModal from '../ShareGuideModal';
import PublishGuideModal from '../PublishGuideModal';
import {
  authHeaders,
  flashcardsFetch,
  formatAttemptDate,
  resolveAttemptScore
} from './flashcardsUtils';
import '../guides.css';

export default function FlashcardSetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [setData, setSetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('test');
  const [filter, setFilter] = useState('all');
  const [shuffled, setShuffled] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [decks, setDecks] = useState([]);
  const [moveSectionId, setMoveSectionId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await flashcardsFetch(`/sets/${id}`, { headers: authHeaders() });
      setSetData(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load set');
      navigate('/guides/flashcards');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!setData) return;
    const canStudy = !!(setData.canStudy ?? setData.canEdit);
    if (canStudy) return;
    const count = setData.cardCount ?? (setData.cards || []).length;
    if (count === 0) return;
    navigate(`/guides/flashcards/sets/${id}/study?view=browse`, { replace: true });
  }, [setData, id, navigate]);

  const togglePublish = async () => {
    if (!setData.published) {
      setPublishOpen(true);
      return;
    }
    if (!window.confirm('Unpublish this flashcard set?')) return;
    try {
      const res = await flashcardsFetch(`/sets/${id}/publish`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ published: false })
      });
      setSetData((s) => ({
        ...s,
        published: res.published,
        publishedAt: res.publishedAt,
        allowEdits: res.allowEdits,
        categoryId: res.categoryId,
        topicId: res.topicId,
        description: res.description ?? s.description
      }));
      toast.success('Unpublished');
    } catch (err) {
      toast.error(err.message || 'Failed to update publish status');
    }
  };

  const beginSession = () => {
    if (!setData) return;
    const cardCount = setData.cardCount ?? (setData.cards || []).length;
    if (cardCount === 0) {
      toast.info('Add cards before starting a session');
      return;
    }
    const params = new URLSearchParams({
      mode,
      filter,
      shuffled: shuffled ? '1' : '0'
    });
    navigate(`/guides/flashcards/sets/${id}/study?${params.toString()}`);
  };

  const viewCards = () => {
    if (!setData) return;
    const cardCount = setData.cardCount ?? (setData.cards || []).length;
    if (cardCount === 0) {
      toast.info('Add cards before viewing');
      return;
    }
    navigate(`/guides/flashcards/sets/${id}/study?view=browse`);
  };

  const openMove = async () => {
    setBusy(true);
    try {
      const mine = await flashcardsFetch('/mine', { headers: authHeaders() });
      const deckItems = (Array.isArray(mine) ? mine : []).filter((i) => i.type === 'deck');
      const loaded = await Promise.all(
        deckItems.map(async (d) => {
          const full = await flashcardsFetch(`/decks/${d.id}`, { headers: authHeaders() });
          return full;
        })
      );
      setDecks(loaded);
      setMoveSectionId(setData?.sectionId ? String(setData.sectionId) : '');
      setMoveOpen(true);
    } catch (err) {
      toast.error(err.message || 'Failed to load decks');
    } finally {
      setBusy(false);
    }
  };

  const submitMove = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await flashcardsFetch(`/sets/${id}/move`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({
          sectionId: moveSectionId === '' ? null : Number(moveSectionId)
        })
      });
      toast.success('Organization updated');
      setMoveOpen(false);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to move set');
    } finally {
      setBusy(false);
    }
  };

  const deleteSet = async () => {
    if (!window.confirm('Delete this flashcard set and all its cards?')) return;
    try {
      await flashcardsFetch(`/sets/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      toast.success('Deleted');
      navigate('/guides/flashcards');
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const stats = useMemo(() => {
    if (!setData) {
      return { cardsReviewed: 0, attemptCount: 0, latestScore: null, latestScoreMode: null, needReview: 0 };
    }
    const attempts = setData.recentAttempts || [];
    const cardsReviewed = attempts.reduce((sum, a) => sum + (a.cardsAttempted || 0), 0);
    const scored = attempts.find((a) => resolveAttemptScore(a) != null);
    return {
      cardsReviewed,
      attemptCount: attempts.length,
      latestScore: scored ? resolveAttemptScore(scored) : null,
      latestScoreMode: scored?.mode || null,
      needReview: (setData.wrongCardIds || []).length
    };
  }, [setData]);

  if (loading || !setData) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Flashcard set" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  const cardCount = setData.cardCount ?? (setData.cards || []).length;
  const correctCount = (setData.correctCardIds || []).length;
  const wrongCount = (setData.wrongCardIds || []).length;
  const attempts = setData.recentAttempts || [];
  const canEdit = !!setData.canEdit;
  const canStudy = !!(setData.canStudy ?? setData.canEdit);
  const isStandalone = !setData.sectionId;
  const backTarget = setData.section?.deck?.id
    ? `/guides/flashcards/decks/${setData.section.deck.id}`
    : canEdit
      ? '/guides/flashcards'
      : setData.published
        ? '/guides/discover'
        : '/guides/shared';
  const locationLabel = setData.section?.deck
    ? `${setData.section.deck.title} → ${setData.section.title}`
    : 'Standalone set';

  const sectionOptions = [];
  decks.forEach((deck) => {
    (deck.sections || []).forEach((sec) => {
      sectionOptions.push({
        id: sec.id,
        label: `${deck.title} / ${sec.title}`
      });
    });
  });

  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={setData.title} />
      <PageTitle title={setData.title} />
      <div className="bg-white border-bottom guides-topic-editor-topbar">
        <Container fluid="lg">
          <div className="d-flex justify-content-between align-items-center py-2 gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-3 min-w-0">
              <Button
                size="sm"
                variant="light-solid"
                className="guides-icon-btn"
                onClick={() => navigate(backTarget)}
              >
                <i className="bi bi-chevron-left" />
              </Button>
              <div className="min-w-0">
                <h6 className="mb-0 fw-semibold text-truncate">{setData.title}</h6>
                <div className="small text-muted text-truncate">{locationLabel}</div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              {canEdit && isStandalone && (
                <div className="radio-inputs guides-topbar-radios">
                  <label className="radio">
                    <input
                      type="checkbox"
                      checked={!!setData.published}
                      onChange={togglePublish}
                      aria-label={setData.published ? 'Published' : 'Publish'}
                    />
                    <span className="name px-3">
                      <i className="bi bi-globe2" aria-hidden />
                      <span className="d-none d-md-inline ms-1">{setData.published ? 'Published' : 'Publish'}</span>
                    </span>
                  </label>
                  <label className="radio">
                    <input
                      type="checkbox"
                      checked={(setData.sharedUserIds || []).length > 0}
                      onChange={() => setShareOpen(true)}
                      aria-label="Shared"
                    />
                    <span className="name px-3">
                      <i className="bi bi-share" aria-hidden />
                      <span className="d-none d-md-inline ms-1">Shared</span>
                    </span>
                  </label>
                </div>
              )}
              {canEdit && (
                <>
                  <Button size="sm" variant="outline-secondary" onClick={openMove} disabled={busy}>
                    Organize
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => navigate(`/guides/flashcards/sets/${id}/edit`)}
                  >
                    Edit cards
                  </Button>
                  <Button size="sm" variant="light-lite danger-lite" onClick={deleteSet}>
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </Container>
      </div>

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <Row className="g-0 h-100">
          <Col lg={3} md={4} xs={12} className="border-end guides-topic-editor-rail fc-learn-rail">
            <div className="fc-learn-panel">
              <h5 className="fc-learn-title mb-1">{canStudy ? 'Learn' : 'Cards'}</h5>
              <p className="fc-learn-subtitle text-muted mb-4">
                {canStudy ? 'Create a new study session' : 'Preview this set without taking a test or review'}
              </p>

              {canStudy && (
                <>
                  <div className="fc-learn-group mb-3">
                    <div className="fc-learn-label">Mode</div>
                    <div className="fc-learn-radios">
                      <Form.Check
                        type="radio"
                        id="fc-mode-test"
                        name="fc-mode"
                        label="Test"
                        checked={mode === 'test'}
                        onChange={() => setMode('test')}
                      />
                      <Form.Check
                        type="radio"
                        id="fc-mode-review"
                        name="fc-mode"
                        label="Review"
                        checked={mode === 'review'}
                        onChange={() => setMode('review')}
                      />
                    </div>
                  </div>

                  <div className="fc-learn-group mb-3">
                    <div className="fc-learn-label">Cards</div>
                    <Form.Select
                      size="sm"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="fc-learn-select"
                    >
                      <option value="all">All cards ({cardCount})</option>
                      <option value="correct" disabled={correctCount === 0}>
                        Correct cards ({correctCount})
                      </option>
                      <option value="wrong" disabled={wrongCount === 0}>
                        Wrong cards ({wrongCount})
                      </option>
                    </Form.Select>
                  </div>

                  <div className="fc-learn-group mb-4">
                    <div className="fc-learn-label">Order</div>
                    <Form.Check
                      type="checkbox"
                      id="fc-shuffle"
                      label="Randomize/shuffle"
                      checked={shuffled}
                      onChange={(e) => setShuffled(e.target.checked)}
                    />
                  </div>

                  <Button
                    variant="primary"
                    className="fc-learn-start-btn w-100"
                    onClick={beginSession}
                    disabled={cardCount === 0}
                  >
                    Start session
                  </Button>
                </>
              )}
              <Button
                variant="outline-secondary"
                className="fc-learn-view-btn w-100 mt-2"
                onClick={viewCards}
                disabled={cardCount === 0}
              >
                View cards
              </Button>

              {cardCount === 0 && canEdit && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="w-100 mt-2"
                  onClick={() => navigate(`/guides/flashcards/sets/${id}/edit`)}
                >
                  Add cards
                </Button>
              )}
              {cardCount === 0 && !canEdit && (
                <p className="small text-muted mt-2 mb-0">This set has no cards yet.</p>
              )}
            </div>
          </Col>

          <Col lg={9} md={8} xs={12} className="guides-topic-editor-col">
            <div className="guides-read-scroll pt-3 pb-5 pe-0">
              {!canStudy ? (
                <div className="guides-empty card border shadow-none p-4">
                  <p className="mb-0 text-muted">
                    Add this set to your collection to take a test or review. You can still view the cards.
                  </p>
                </div>
              ) : (
              <>
              <div className="fc-stats-banner">
                <div className="fc-stats-banner-intro">
                  <div className="fc-stats-banner-icon" aria-hidden>
                    <i className="bi bi-graph-up" />
                  </div>
                  <div>
                    <div className="fc-stats-banner-title">Your stats</div>
                    <div className="fc-stats-banner-desc">
                      Your general stats based on your study sessions
                    </div>
                  </div>
                </div>
                <div className="fc-stats-banner-grid">
                  <div className="fc-stats-banner-item">
                    <div className="fc-stats-banner-value">{stats.cardsReviewed}</div>
                    <div className="fc-stats-banner-meta">Cards Reviewed</div>
                  </div>
                  <div className="fc-stats-banner-item">
                    <div className="fc-stats-banner-value">{stats.attemptCount}</div>
                    <div className="fc-stats-banner-meta">Number of attempts</div>
                  </div>
                  <div className="fc-stats-banner-item">
                    <div className="fc-stats-banner-value">
                      {stats.latestScore != null ? `${stats.latestScore}%` : '—'}
                    </div>
                    <div className="fc-stats-banner-meta">
                      {stats.latestScoreMode === 'review'
                        ? 'Score review mode'
                        : stats.latestScoreMode === 'test'
                          ? 'Score test mode'
                          : 'Latest score'}
                    </div>
                  </div>
                  <div className="fc-stats-banner-item">
                    <div className="fc-stats-banner-value">{stats.needReview}</div>
                    <div className="fc-stats-banner-meta">Need to review</div>
                  </div>
                </div>
              </div>

              <h6 className="fw-semibold mb-3 mt-4">Recent attempts</h6>

              {attempts.length === 0 ? (
                <div className="guides-empty card border shadow-none p-4">
                  <p className="mb-0 text-muted">
                    No previous attempts yet. Use Learn to start a session.
                  </p>
                </div>
              ) : (
                <div className="fc-attempt-list">
                  {attempts.map((a) => {
                    const isTest = a.mode === 'test';
                    const knownOrCorrect = a.correctCount ?? 0;
                    const total = a.cardsAttempted ?? 0;
                    const score = resolveAttemptScore(a);
                    return (
                      <div key={a.id} className="fc-attempt-card">
                        <div
                          className={`fc-attempt-icon${isTest ? ' is-test' : ' is-review'}`}
                          aria-hidden
                        >
                          <i className={`bi ${isTest ? 'bi-list-check' : 'bi-chat-quote'}`} />
                        </div>
                        <div className="fc-attempt-main min-w-0">
                          <div className="fc-attempt-title text-capitalize">{a.mode}</div>
                          <div className="fc-attempt-date">
                            {formatAttemptDate(a.completedAt || a.createdAt)}
                          </div>
                        </div>
                        <div className="fc-attempt-mid text-muted small">
                          {a.shuffled ? 'Shuffled' : 'Original order'}
                          {a.filter && a.filter !== 'all' ? ` · ${a.filter}` : ''}
                        </div>
                        <div className="fc-attempt-counts small text-muted">
                          {isTest
                            ? `${knownOrCorrect}/${total} correct`
                            : `${knownOrCorrect}/${total} known`}
                        </div>
                        <div className="fc-attempt-score">
                          {score != null ? (
                            <>
                              <div className="fc-attempt-score-value">{score}%</div>
                              <div className="fc-attempt-score-label">Score</div>
                            </>
                          ) : (
                            <>
                              <div className="fc-attempt-score-value text-muted">—</div>
                              <div className="fc-attempt-score-label">Score</div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              </>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <Modal show={moveOpen} onHide={() => setMoveOpen(false)} centered>
        <Form onSubmit={submitMove}>
          <Modal.Header closeButton>
            <Modal.Title>Organize set</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Label className="small text-muted">Place in section</Form.Label>
            <Form.Select
              value={moveSectionId}
              onChange={(e) => setMoveSectionId(e.target.value)}
            >
              <option value="">Standalone (no deck)</option>
              {sectionOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </Form.Select>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {isStandalone && (
        <>
          <ShareGuideModal
            show={shareOpen}
            onHide={() => setShareOpen(false)}
            targetType="flashcard_set"
            targetId={Number(id)}
            initialUserIds={setData.sharedUserIds || []}
            onSaved={(shares) => setSetData((s) => ({
              ...s,
              sharedUserIds: (shares || []).map((share) => share.userId)
            }))}
          />
          <PublishGuideModal
            show={publishOpen}
            onHide={() => setPublishOpen(false)}
            targetType="flashcard_set"
            targetId={Number(id)}
            initialDescription={setData.description || ''}
            initialCategoryId={setData.categoryId}
            initialTopicId={setData.topicId}
            initialAllowEdits={!!setData.allowEdits}
            onPublished={(res) => {
              setSetData((s) => ({
                ...s,
                published: true,
                publishedAt: res.publishedAt,
                allowEdits: res.allowEdits,
                categoryId: res.categoryId,
                topicId: res.topicId,
                description: res.description ?? s.description
              }));
            }}
          />
        </>
      )}
    </div>
  );
}
